/**
 * Tipos para o fluxo de geração do Laudo de Avaliação
 */

import type { LiquidezImovel } from '../utils/liquidez';

export type TipoImovelAmostra = string;

// ============================================
// ETAPA 0: Checklist (Videoconferência)
// ============================================
export interface ChecklistData {
  checklistId: string;
  checklistPreenchido: boolean;
  completed?: boolean;
  respostas: any[]; // ChecklistAnswer[]
}

// ============================================
// ETAPA 1: Identificação
// ============================================
export interface IdentificacaoData {
  proponente: string;
  enderecoCompleto: string;
  // Campos individuais de endereço para sincronização com outros steps
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  matriculaNumero: string;
  matriculaCartorio: string;
  /** Nº do IPTU editado na tela de Identificação. Exibição no PDF; não entra nos cálculos. */
  iptuNumero?: string;
  areaTotal: number;
  /**
   * Área construída informada na tela de Identificação. É só para exibição no bloco
   * "Dados do Imóvel" do PDF — a área construída usada nos cálculos continua sendo a do
   * step Avaliação (`avaliacao.dadosImovel.areaConstruida`).
   */
  areaConstruida?: number;
  areaPrivativa: number;
  tipoSolicitacao: string;
  tipoImovel?: string;
}

// ============================================
// ETAPA 2: Vistoria
// ============================================
export interface VistoriaData {
  checklistId: string;
  checklistRespostas: Record<string, any>;
  processedResponses?: any[]; // Respostas do checklist com URLs de fotos já renovadas/convertidas
  fotoFachada?: string; // URL ou base64 (temporário, não salvo no reportData)
  fotoFachadaDocumentId?: string; // ID do documento no S3 (salvo no reportData)
  fotoFachadaCaption?: string; // Legenda da foto de fachada
  dataVistoria: string;
  vistoriador: string;
  photoOrder?: string[]; // IDs das fotos na ordem desejada para o relatório fotográfico
}

// ============================================
// ETAPA 3: Avaliação
// ============================================
export interface DadosImovelAvaliacao {
  tipoImovel: string; // dropdown
  tipoImplantacao: string; // dropdown
  areaConstruida: number; // input numérico (m²)
  quantidadeQuartos: number; // dropdown
  quantidadeBanheiros: number; // dropdown
  quantidadeVagas: number; // dropdown
  idadeAparente: number; // input numérico (anos)
  estadoConservacao: string; // dropdown
  padraoAcabamento: string; // dropdown
  // dropdown fechado (Lenta | Normal | Rápida), obrigatório para finalizar. Opcional no tipo
  // porque rascunhos e laudos antigos não têm o campo (ou guardam o fator numérico antigo).
  fatorLiquidez?: LiquidezImovel;
}

export interface DadosTerrenoAvaliacao {
  areaTerreno?: number; // input numérico (m²) — opcional (imóveis sem terreno próprio, ex: apartamento)
  testada?: number; // input numérico (m) — opcional (idem)
  padraoTerreno: string; // dropdown
  topografia: string; // dropdown
}

export interface DadosCondominioAvaliacao {
  areaComum: number; // numérico
  estadoConservacao: string; // dropdown
}

export interface AvaliacaoData {
  dadosImovel: DadosImovelAvaliacao;
  dadosTerreno: DadosTerrenoAvaliacao;
  dadosCondominio: DadosCondominioAvaliacao;
  _initialized?: boolean; // Flag para indicar que os dados já foram carregados/editados
}

// ============================================
// ETAPA 4: Amostras
// ============================================
export interface EnderecoAmostra {
  rua: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

export interface DadosAmostra {
  tipoImovel: string;
  areaPrivativa: number;
  areaTerreno?: number;
  areaCoberta?: number;
  quantidadeQuartos: number;
  quantidadeBanheiros: number;
  quantidadeVagas: number;
  andar?: number;
  padraoAcabamento: string;
  estadoConservacao: string;
  valorAnunciado: number;
  valorM2: number; // calculado
  fonteAnuncio: string; // Ex: VivaReal, Zap, Manual
  urlAnuncio?: string;
  dataAnuncio: string;
  imagemUrl?: string; // Foto do imóvel vinda do scraping
  selecionadaHomogeneizacao?: boolean; // Se está selecionada para o cálculo de homogeneização
  rankingHomogeneizacao?: number; // Posição no ranking (1 = melhor, 2 = segunda melhor, etc.)
}

export interface Amostra {
  id: string;
  externalId?: string;
  endereco: EnderecoAmostra;
  dados: DadosAmostra;
  imagemUrl?: string;
  coordenadas?: {
    latitude: number;
    longitude: number;
  };
}

// Dados para método evolutivo (CUB)
export interface DadosAreaConstruida {
  identificacao: string; // "Avaliando" ou "#1", "#2", etc.
  tipoImovel: TipoImovelAmostra; // tipo do imóvel
  areaConstruida: number; // m²
  cubBase: number; // R$/m²
  ajusteAcabamento: number; // fator multiplicador (ex: 1.5)
  depreciacao: number; // fator de 0 a 1 (ex: 0.313)
  cubAjustado: number; // calculado
  valorFinal: number; // calculado
}

// Dados para cálculo de depreciação (Ross-Heidecke)
export interface DadosDepreciacao {
  identificacao: string; // "Avaliando" ou "#1", "#2", etc.
  tipoImovel: TipoImovelAmostra; // tipo do imóvel
  idadeAparente: number; // idade física percebida (anos)
  estadoConservacao: string; // estado de manutenção
  vidaUtil: number; // expectativa total de vida útil (anos) - editável
  valorResidual: number; // valor residual (ex: 0.2 = 20%)
  coefHeidecke: number; // coeficiente baseado no estado de conservação (calculado)
  porcentagemVU: number; // % da vida útil transcorrida (calculado)
  depreciacao: number; // coeficiente final de depreciação (0 a 1, calculado)
}

// Switches para controlar fatores ativos na tabela de terreno
export interface SwitchesValorTerreno {
  area: boolean;
  testada: boolean;
  vegetacao: boolean;
  fatorExtraI: boolean;
  fatorExtraII: boolean;
  fatorExtraIII: boolean;
}

// Dados para cálculo de valor do terreno
export interface DadosValorTerreno {
  amostra: string; // "#1", "#2", etc.
  tipoImovel: TipoImovelAmostra; // tipo de imóvel (padronizado)
  areaTerreno: number; // m²
  padrao: string; // classificação urbanística
  topografia: string; // tipo de relevo
  valorTotalAmostra: number; // R$ - Valor total da amostra (antes de subtrair construção)
  valorTerreno: number; // R$ - Calculado: valorTotalAmostra - valor da construção
  valorUnitario: number; // calculado: valorTerreno / areaTerreno
  indiceFiscal: number; // fator de correção
  fatorOferta: number; // correção oferta vs real
  fatorTopografia: number; // ajuste por relevo
  fatorArea: number; // opcional
  fatorTestada: number; // opcional
  fatorVegetacao: number; // opcional
  fatorExtraI: number; // opcional
  fatorExtraII: number; // opcional
  fatorExtraIII: number; // opcional
  fat: number; // calculado: produto dos fatores físicos apenas
  valorUnitarioFinal: number; // calculado: valorUnitario × indiceFiscal × fatorOferta × fat
}

// Dados para método comparativo direto de mercado (homogeneização)
export interface DadosComparativoDireto {
  amostra: string; // "Avaliando", "#1", "#2", etc.
  area: number; // m²
  valorAnuncio: number; // R$ - valor do anúncio
  valorM2: number; // calculado: valorAnuncio / area
  fatorOferta: number; // fator de oferta (ex: 0.9)
  fatorRegiao: number; // fator ajuste região (se selecionado)
  fatorAndar: number; // fator ajuste andar (se selecionado)
  fatorVaga: number; // fator ajuste vaga (se selecionado)
  fatorArea: number; // fator ajuste área (se selecionado)
  fatorPadraoAcabamento: number; // fator ajuste padrão (se selecionado)
  fatorEstadoConservacao: number; // fator ajuste estado (se selecionado)
  fatorExtraI: number; // fator extra I (opcional)
  fatorExtraII: number; // fator extra II (opcional)
  fatorExtraIII: number; // fator extra III (opcional)
  fat: number; // calculado: produto de todos os fatores selecionados
  valorHomogeneizado: number; // calculado: valorM2 × fat
}

export interface AmostrasData {
  amostras: Amostra[];
  metodologiaImportacao: 'manual' | 'planilha' | 'mapa';
  // Configuração e Fatoração (movido do step de Metodologia)
  metodologia?: string; // Ex: "Comparativo Direto de Mercado"
  parametrosFatoracao?: string[]; // Ex: ['regiao', 'andar', 'vaga']
  // Dados do método evolutivo (área construída)
  dadosAreaConstruida: DadosAreaConstruida[];
  // Dados de depreciação (Ross-Heidecke) - um para cada amostra
  dadosDepreciacao: DadosDepreciacao[];
  // Dados do método comparativo (valor do terreno)
  dadosValorTerreno: DadosValorTerreno[];
  switchesTerreno: SwitchesValorTerreno;
  // Dados para método comparativo direto de mercado
  dadosComparativoDireto: DadosComparativoDireto[];
  // Classificações para Cálculos Avaliatórios
  grauPrecisao?: string;
  grauFundamentacao?: string;
  // Área Construída Não Averbada
  especificarAreaNaoAverbada?: boolean;
  areaNaoAverbada?: number; // m²
  fatorAjusteNaoAverbada?: number; // fator de correção (ex: 0.64)
  // Comentários e Observações
  comentarios?: string;
  // Flag para indicar que a prestadora pulou os cálculos (serão feitos pelo banco ou outra prestadora)
  skipCalculations?: boolean;
}

// ============================================
// STEP 5: Signature
// ============================================
export interface SignatureData {
  evaluatorName: string; // required
  creaCauNumber: string; // CREA/CAU
  signatureImage: string; // base64 (required)
  _saved?: boolean; // Flag interna: indica se os dados foram salvos (usuário clicou em "Salvar")
}

// ============================================
// ETAPA 6: Transmissão
// ============================================
export interface TransmissaoData {
  transmissao: {
    sistemaDestino: string; // Ex: Salesforce
    dataTransmissao?: string;
    statusTransmissao: 'pendente' | 'enviado' | 'erro';
  };
  pdfGerado: boolean;
  urlDownload?: string;
  processedLaudo?: any; // Dados do laudo processado para visualização
  logoBase64?: string; // Logo em base64 para o PDF
  streetViewImageBase64?: string; // Foto de fachada via Street View para o layout AVM
  processingUrls?: boolean; // Estado de processamento
  generalComments?: string; // Comentários gerais da revisão (modo review)
}

// ============================================
// ESTADO COMPLETO DO LAUDO
// ============================================
export interface LaudoWizardData {
  checklist: ChecklistData;
  identificacao: IdentificacaoData;
  vistoria: VistoriaData;
  avaliacao: AvaliacaoData;
  amostras: AmostrasData;
  signature: SignatureData;
  transmissao: TransmissaoData;
}

// ============================================
// CONTROLE DO WIZARD
// ============================================
export enum LaudoStep {
  CHECKLIST = 0,
  IDENTIFICACAO = 1,
  VISTORIA = 2,
  AVALIACAO = 3,
  AMOSTRAS = 4,
  ASSINATURA = 5,
  TRANSMISSAO = 6,
}

export interface StepConfig {
  key: LaudoStep;
  title: string;
  description: string;
  icon?: string;
  isOptional?: boolean;
  isReadonly?: boolean;
}

// ============================================
// WIZARD MODES
// ============================================
export type WizardMode = 'create' | 'edit' | 'review' | 'correction' | 'editAutomatic' | 'calculations';

export interface WizardReviewContext {
  reportId: string;
  reviewerType: 'Provider' | 'Bank';
  pendingCorrections?: Array<{
    fieldPath: string;
    fieldLabel: string;
    errorMessage: string;
  }>;
  externalReturn?: {
    reason: string;
    description: string | null;
  };
}

export const LAUDO_STEPS_CONFIG: StepConfig[] = [
  {
    key: LaudoStep.IDENTIFICACAO,
    title: 'Identificação',
    description: 'Dados básicos do imóvel e proponente',
    isReadonly: true,
  },
  {
    key: LaudoStep.VISTORIA,
    title: 'Vistoria',
    description: 'Checklist técnico e foto da fachada',
    isReadonly: true,
  },
  {
    key: LaudoStep.AVALIACAO,
    title: 'Avaliação',
    description: 'Dados técnicos e características',
  },
  {
    key: LaudoStep.AMOSTRAS,
    title: 'Amostras',
    description: 'Imóveis comparativos',
  },
  {
    key: LaudoStep.ASSINATURA,
    title: 'Assinatura',
    description: 'Dados do avaliador e assinatura',
  },
  {
    key: LaudoStep.TRANSMISSAO,
    title: 'Transmissão',
    description: 'Finalização e envio',
  },
];
